/**
 * **Personal-data erasure** (`/settings/erasure`, FR-28, D25; W28, FR-65,
 * plan D86) — a recorded DPDP request, never an ad-hoc delete, and no bar: the
 * act keeps its dialog because it asks for step-up. The organisation finds the
 * principal's documents by search (v1 reads no content), names them under its
 * own reference, and re-verifies. Each document past its statutory floor is
 * erased now; one within it is **held**, with the day and the law that hold
 * it, and the daily job completes it then. `docs/DPDP_ERASURE.md` is the
 * procedure.
 *
 * The settings administrator's alone (`mayErase`): anyone else is told so and
 * the requests are not read.
 */

import {
  DOCUMENT,
  MAX_ERASURE_REFERENCE,
  errorText,
  formatBytes,
  isStepUpCancelled,
  mayErase,
  useLoad,
  withStepUp,
  type ErasureRequest,
  type ProDocClient,
  type ProDocDocument,
  type StepUpAsk,
  type Verb,
} from '@fiveox/prodoc-ui'
import { Alert, Badge, Button, Checkbox, Dialog, EmptyState, Input, SearchInput, Skeleton, SkeletonRegion, Textarea, formatDate, formatDateTime } from '@fiveox/ui'
import { ShieldCheck, UserX } from 'lucide-react'
import { useEffect, useState } from 'react'

import { EditPage } from '../../components/edit'
import { SettingsSection } from '../../components/settings-parts'
import { useSettingsHost } from './host'
import { SETTINGS_BACK } from './settings-data'

export function ErasureSettings() {
  const { client, can, requestStepUp } = useSettingsHost()
  return (
    <EditPage
      back={SETTINGS_BACK}
      title="Personal-data erasure"
      description="Erase a person’s documents on their request (DPDP). What the law still requires is held until its date, and the request says so."
    >
      {/* D25: the settings administrator's — anyone else is told so, and the requests are not read. */}
      {mayErase(can) ? (
        <ErasureRequests client={client} can={can} requestStepUp={requestStepUp} />
      ) : (
        <EmptyState title="Erasure is the administrator’s" description="Only a ProDoc administrator records an erasure request, re-verifying to do it." />
      )}
    </EditPage>
  )
}

/** The organisation's requests, newest first as the service lists them, and the act that records one. */
function ErasureRequests({ client, can, requestStepUp }: { client: ProDocClient; can: (resource: string, verb: Verb) => boolean; requestStepUp?: StepUpAsk }) {
  const requests = useLoad(() => client.erasure.list(), [client])
  const [open, setOpen] = useState(false)
  const [shown, setShown] = useState<ErasureRequest | null>(null)
  return (
    <>
      <SettingsSection
        id="requests"
        icon={<UserX size={18} aria-hidden />}
        title="Erasure requests"
        description="Each under the organisation’s own reference, with what each of its documents came to."
        action={
          <Button variant="outline" size="sm" onClick={() => setOpen(true)}>
            <ShieldCheck size={15} aria-hidden />
            Record a request
          </Button>
        }
      >
        {requests.loading && !requests.data ? (
          <SkeletonRegion label="Loading erasure requests" className="space-y-2">
            <Skeleton className="h-5 w-72" />
            <Skeleton className="h-5 w-60" />
          </SkeletonRegion>
        ) : requests.error && !requests.data ? (
          <Alert tone="error">
            Could not load the erasure requests: {requests.error}{' '}
            <Button variant="link" onClick={() => void requests.reload()}>
              Try again
            </Button>
          </Alert>
        ) : (requests.data ?? []).length === 0 ? (
          <EmptyState variant="outline" title="No erasure requests" description="When a person asks for their data to be erased, record it here against your own reference." />
        ) : (
          <ul className="divide-border divide-y" aria-label="Erasure requests">
            {(requests.data ?? []).map((r) => (
              <li key={r.id} className="flex flex-wrap items-center justify-between gap-3 py-2.5 first:pt-0 last:pb-0">
                <div className="min-w-0">
                  <p className="text-foreground text-sm font-medium">{r.reference}</p>
                  <p className="text-muted-foreground text-xs">
                    {r.created_at ? formatDateTime(r.created_at) : ''} · {r.document_count} document{r.document_count === 1 ? '' : 's'}: {r.erased_count} erased
                    {r.held_count ? `, ${r.held_count} held by law` : ''}
                    {r.completed_at ? ' · complete' : ''}
                  </p>
                </div>
                <Button variant="ghost" size="sm" onClick={() => void client.erasure.get(r.id).then(setShown, () => setShown(r))}>
                  Open
                </Button>
              </li>
            ))}
          </ul>
        )}
      </SettingsSection>
      <ErasureDialog
        open={open}
        client={client}
        can={can}
        requestStepUp={requestStepUp}
        onClose={() => setOpen(false)}
        onRecorded={(r) => {
          setShown(r)
          void requests.reload()
        }}
      />
      <Dialog open={shown !== null} onClose={() => setShown(null)} title={shown ? `Erasure request ${shown.reference}` : 'Erasure request'} width="lg">
        {shown && <ErasureOutcome request={shown} />}
      </Dialog>
    </>
  )
}

const OUTCOME: Record<string, { label: string; tone: 'success' | 'warning' | 'neutral' }> = {
  erased: { label: 'Erased', tone: 'success' },
  held_by_floor: { label: 'Held by law', tone: 'warning' },
  already_expired: { label: 'Already removed', tone: 'neutral' },
}

/** What each document came to — and for a held one, until when, and which law holds it. */
export function ErasureOutcome({ request, titles = {} }: { request: ErasureRequest; titles?: Record<string, string> }) {
  return (
    <div className="space-y-3">
      <p className="text-muted-foreground text-sm">
        {request.erased_count} erased{request.held_count ? `, ${request.held_count} held until the law lets them go` : ''}. {request.completed_at ? `Complete since ${formatDateTime(request.completed_at)}.` : request.held_count ? 'The daily job erases each held document on its date.' : ''}
      </p>
      <ul className="divide-border divide-y" aria-label="Documents of the request">
        {request.items.map((item) => (
          <li key={item.document_id} className="flex flex-wrap items-start justify-between gap-2 py-2">
            <div className="min-w-0">
              <p className="text-foreground text-sm">{titles[item.document_id] ?? `Document …${item.document_id.slice(-6)}`}</p>
              {item.outcome === 'held_by_floor' && (
                <p className="text-muted-foreground text-xs">
                  Held until {item.floor_until ? formatDate(item.floor_until) : 'its floor'}
                  {item.floor_rules.length ? ` — ${item.floor_rules.map((f) => f.citation).join('; ')}` : ''}
                </p>
              )}
              {item.erased_at && <p className="text-muted-foreground text-xs">Erased {formatDateTime(item.erased_at)}</p>}
            </div>
            <Badge tone={OUTCOME[item.outcome]?.tone ?? 'neutral'} size="sm">
              {OUTCOME[item.outcome]?.label ?? item.outcome}
            </Badge>
          </li>
        ))}
      </ul>
    </div>
  )
}

/** Find the principal's documents, name the request, re-verify, record — then show what each came to. */
function ErasureDialog({
  open,
  client,
  can,
  requestStepUp,
  onClose,
  onRecorded,
}: {
  open: boolean
  client: ProDocClient
  can: (resource: string, verb: Verb) => boolean
  requestStepUp?: StepUpAsk
  onClose: () => void
  onRecorded: (request: ErasureRequest) => void
}) {
  const [reference, setReference] = useState('')
  const [reason, setReason] = useState('')
  const [q, setQ] = useState('')
  const [asked, setAsked] = useState('')
  const [chosen, setChosen] = useState<Map<string, ProDocDocument>>(new Map())
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [hidden, setHidden] = useState(false)
  // Archived and expired documents are a principal's too (an expired record is still scrubbed, D25);
  // only the archive authority reads them.
  const statuses = can(DOCUMENT, 'can_delete') ? (['stored', 'archived', 'expired'] as const) : (['stored'] as const)
  const found = useLoad(
    async () => (asked ? (await Promise.all(statuses.map((status) => client.documents.list({ q: asked, status, limit: 25 })))).flatMap((p) => p.items) : []),
    [client, asked],
  )

  useEffect(() => {
    if (open) return
    setReference('')
    setReason('')
    setQ('')
    setAsked('')
    setChosen(new Map())
    setError('')
    setBusy(false)
    setHidden(false)
  }, [open])

  const toggle = (doc: ProDocDocument, on: boolean) =>
    setChosen((c) => {
      const next = new Map(c)
      if (on) next.set(doc.id, doc)
      else next.delete(doc.id)
      return next
    })

  const record = async () => {
    setBusy(true)
    setError('')
    try {
      const request = await withStepUp(
        (assertion) =>
          client.erasure.create({ reference: reference.trim(), reason: reason.trim() || undefined, document_ids: [...chosen.keys()] }, { stepUpAssertion: assertion }),
        requestStepUp
          ? async (r) => {
              // The step-up dialog stands alone while it is open; this one waits, its fields kept.
              setHidden(true)
              try {
                return await requestStepUp(r)
              } finally {
                setHidden(false)
              }
            }
          : undefined,
        { title: 'Record an erasure request', what: `Erase ${chosen.size} document${chosen.size === 1 ? '' : 's'} under “${reference.trim()}”. What the law still requires is held until its date; the rest cannot be brought back.` },
        true,
      )
      onClose()
      onRecorded(request)
    } catch (e) {
      setError(isStepUpCancelled(e) ? 'Nothing was recorded — the re-verification was cancelled.' : errorText(e))
    } finally {
      setBusy(false)
    }
  }

  const refError = reference.trim().length > MAX_ERASURE_REFERENCE ? `At most ${MAX_ERASURE_REFERENCE} characters.` : null
  return (
    <Dialog open={open && !hidden} onClose={onClose} title="Record an erasure request" error={error || null} width="lg">
      <div className="space-y-4">
        <Input label="Your reference" value={reference} onChange={(e) => setReference(e.target.value)} placeholder="DPDP-2026-014" error={refError} hint="The organisation’s own reference for the person’s request. It names no one." />
        <Textarea label="Note (optional)" rows={2} maxLength={500} value={reason} onChange={(e) => setReason(e.target.value)} />
        <div className="space-y-2">
          <p className="text-foreground text-sm font-medium">The person’s documents</p>
          <form
            role="search"
            className="flex flex-wrap items-center gap-2"
            onSubmit={(e) => {
              e.preventDefault()
              setAsked(q.trim())
            }}
          >
            <SearchInput value={q} onChange={setQ} placeholder="Title, file name, reference, notes…" label="Find documents" />
            <Button type="submit" variant="outline" size="sm" disabled={!q.trim()}>
              Search
            </Button>
          </form>
          {asked &&
            (found.loading && !found.data ? (
              <SkeletonRegion label="Searching">
                <Skeleton className="h-5 w-64" />
              </SkeletonRegion>
            ) : found.error ? (
              <Alert tone="error">{found.error}</Alert>
            ) : (found.data ?? []).length === 0 ? (
              <p className="text-muted-foreground text-sm">Nothing you can see matches “{asked}”.</p>
            ) : (
              <ul className="border-border max-h-60 divide-y overflow-y-auto rounded-lg border" aria-label="Search results">
                {(found.data ?? []).map((d) => (
                  <li key={d.id} className="px-3 py-2">
                    <Checkbox
                      label={
                        <>
                          <span className="text-foreground">{d.title}</span>{' '}
                          <span className="text-muted-foreground text-xs">
                            {d.file_name} · {formatBytes(d.size_bytes)}
                            {d.status !== 'stored' ? ` · ${d.status}` : ''}
                          </span>
                        </>
                      }
                      checked={chosen.has(d.id)}
                      onChange={(e) => toggle(d, e.target.checked)}
                    />
                  </li>
                ))}
              </ul>
            ))}
          <p className="text-muted-foreground text-xs">{chosen.size ? `${chosen.size} chosen: ${[...chosen.values()].map((d) => d.title).join(', ')}` : 'None chosen yet.'}</p>
        </div>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="destructive"
            loading={busy}
            loadingText="Recording…"
            disabled={!reference.trim() || chosen.size === 0 || Boolean(refError)}
            onClick={() => void record()}
          >
            Re-verify and record
          </Button>
        </div>
      </div>
    </Dialog>
  )
}
